import React from 'react';
import { api } from '../../lib/api.js';
import { normalizeUrl, isHubspotUrl, isWebUrl } from '../../lib/url.js';
import { EditableGroup } from '../../components/EditableGroup.jsx';
import { EditableList } from '../../components/EditableList.jsx';
import { UserMultiSelect } from '../../components/UserMultiSelect.jsx';
import { nextMilestoneLabel } from './LifecycleTimeline.jsx';
import { ProjectResources } from './ProjectResources.jsx';
import { StatusBadge } from '../Projects.jsx';
import { TierSelect } from '../../components/ui.jsx';
import { Select } from '../../components/controls/Select.jsx';
import { DatePicker } from '../../components/controls/DatePicker.jsx';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
];
const LIFECYCLE_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'support', label: 'Support' },
  { value: 'closed', label: 'Closed' },
];
const BILLABLE_OPTIONS = [
  { value: 'yes', label: 'Client work' },
  { value: 'no', label: 'Non-client work' },
];
const YES_NO_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'yes', label: 'Yes' },
];

// Paid absence counts toward total hours like work; unpaid absence is shown
// apart and never counted (lib/workTypes.js).
const ABSENCE_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'paid', label: 'Paid absence (counts toward total)' },
  { value: 'unpaid', label: 'Unpaid absence (not in total)' },
];

function absenceChoice(project) {
  if (!project.absence) return 'no';
  return project.absencePaid === false ? 'unpaid' : 'paid';
}

const CHANNELS = ['', 'Teams', 'Email', 'Phone', 'Other'];
const LIFECYCLE_LABEL = {
  delivery: 'Delivery',
  support: 'Support',
  closed: 'Closed',
};

const namesFor = (users, ids) =>
  (ids || [])
    .map((id) => users.find((user) => user.id === id)?.name)
    .filter(Boolean);

// The bootstrap user list is scoped (a non-admin PM sees only themselves,
// their teams, and users with time on the project). Merge in the people the
// detail payload reports as assigned so a role picker can still show and
// remove an out-of-scope assignee, then every active person so a PM can pick
// a colleague who has not logged time here yet.
const mergeUsers = (base, ...extras) => {
  const seen = new Set(base.map((user) => user.id));
  const merged = [...base];
  for (const user of extras.flatMap((extra) => extra || [])) {
    if (seen.has(user.id)) continue;
    seen.add(user.id);
    merged.push(user);
  }
  return merged;
};

const orDash = (value) =>
  value ? value : <span className="muted-text">—</span>;

// ---------------------------------------------------------------------------
// List field descriptors
// ---------------------------------------------------------------------------

const STAKEHOLDER_FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'organisation', label: 'Organisation' },
  {
    key: 'preferredChannel',
    label: 'Preferred channel',
    type: 'select',
    options: CHANNELS,
  },
  {
    key: 'responsibility',
    label: 'Responsibility',
    type: 'textarea',
    full: true,
  },
];

const resourceToForm = (r) => ({
  category: r.category || 'Delivery',
  title: r.title || '',
  resourceType: r.resourceType || '',
  owner: r.owner || '',
  lastReviewed: r.lastReviewed || '',
  url: r.url || '',
  description: r.description || '',
  keyDocument: Boolean(r.keyDocument),
});
const resourceFromForm = (f) => ({
  category: f.category || 'Delivery',
  title: (f.title || '').trim(),
  resourceType: (f.resourceType || '').trim(),
  owner: (f.owner || '').trim(),
  lastReviewed: f.lastReviewed || null,
  url: normalizeUrl(f.url),
  description: (f.description || '').trim(),
  keyDocument: Boolean(f.keyDocument),
});
const MILESTONE_FIELDS = [
  { key: 'milestoneType', label: 'Milestone' },
  { key: 'plannedDate', label: 'Planned date', type: 'date' },
  { key: 'amount', label: 'Value' },
  { key: 'currency', label: 'Currency' },
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: ['Planned', 'Under discussion', 'Upcoming', 'Invoiced', 'Paid'],
  },
  { key: 'note', label: 'Note', type: 'textarea', full: true },
];

// ---------------------------------------------------------------------------

export function ProjectInfoTab({
  project,
  data,
  detail,
  reload,
  reloadDetail,
  savePayments,
  savingPayments,
  lockSchedule,
  savingSchedule,
  editingGroup,
  setEditingGroup,
}) {
  const canManageAll = data.canManageAll;
  const users = mergeUsers(data.users, detail.assignedUsers, detail.people);

  // Each profile group's Save rebuilds the whole project/profile body from the
  // current snapshot, so two groups saving before the first reload lands would
  // revert each other. Only let one group be in edit mode at a time. This lock
  // is owned by ProjectDetail (not local state here): this tab unmounts on
  // every tab switch, and a component-local lock would forget an in-flight
  // save the moment the user switches away and back, letting a second group
  // submit its own stale snapshot and silently revert the first.
  //
  // Basics, Roles and Commercial all end up sending a full `putProject`, which
  // always resends the current start/end dates from this snapshot — so they
  // also defer to `savingSchedule` (a Memory-tab schedule PATCH in flight)
  // the same way Timeline already does, or a finishing PUT could restore the
  // dates the Memory tab just changed.
  const groupLock = (key) => ({
    canEdit: (editingGroup === null || editingGroup === key) && !savingSchedule,
    onEditingChange: (on) => setEditingGroup(on ? key : null),
  });

  // Every profile group rebuilds the whole project body from what's in memory
  // and overrides just the fields it owns (PUT /projects/:id wants it all).
  async function putProject(overrides) {
    const hubspotUrl = normalizeUrl(
      'hubspotUrl' in overrides
        ? overrides.hubspotUrl
        : project.hubspotUrl || '',
    );
    if (hubspotUrl && !isHubspotUrl(hubspotUrl)) {
      throw new Error('HubSpot must be a hubspot.com link (or left empty).');
    }
    await api(`/projects/${project.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        clientId: project.clientId,
        code: project.code,
        name: project.name,
        groupName: project.groupName || '',
        billable: project.billable,
        supportProject: project.supportProject,
        color: project.color,
        budgetMD: project.budgetMD,
        startDate: project.startDate || null,
        endDate: project.endDate || null,
        status: project.status,
        managerIds: detail.managerIds || [],
        ...overrides,
        hubspotUrl,
      }),
    });
    // Basics/Commercial fields live on the bootstrap-cached `project`, but
    // Roles/Timeline (this same PUT, when managerIds/status change) now read
    // from `detail` — refresh both, or a save would only reflect itself after
    // the tab is reopened.
    await Promise.all([reload(), reloadDetail()]);
  }

  async function putProfile(overrides) {
    await api(`/projects/${project.id}/profile`, {
      method: 'PUT',
      body: JSON.stringify({
        lifecycleStage: detail.lifecycleStage || null,
        accountManagerIds: detail.accountManagerIds || [],
        supportTeamIds: detail.supportTeamIds || [],
        technicalLeadIds: detail.technicalLeadIds || [],
        ...overrides,
      }),
    });
    // lifecycleStage/role ids are read from `detail`, not the bootstrap-cached
    // `project` — see the note in putProject above.
    await Promise.all([reload(), reloadDetail()]);
  }

  const saveList = (path) => async (items) => {
    await api(`/projects/${project.id}/${path}`, {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
    await reloadDetail();
  };

  // Tier is read from `detail` (fresh on every open) rather than the
  // bootstrap-cached `project`, so an MCP change isn't reverted by a stale
  // value. putProject leaves it out unless Basics sends it; the API then
  // keeps the stored tier.
  const currentTier =
    detail.tier !== undefined ? detail.tier : (project.tier ?? null);
  const hasBudget = project.budgetMD > 0;
  const nextMs = nextMilestoneLabel(
    detail.memory.lifecycleMilestones,
    detail.paymentMilestones,
  );

  return (
    <div className="project-info-tab">
      <section className="card">
        <div className="card-head">
          <h2>Project profile</h2>
        </div>
        <div className="info-groups">
          <EditableGroup
            title="Basics"
            {...groupLock('basics')}
            initialForm={() => ({
              name: project.name,
              code: project.code,
              clientId: project.clientId,
              groupName: project.groupName || '',
              tier: currentTier,
              hubspotUrl: project.hubspotUrl || '',
            })}
            onSave={(f) =>
              putProject({
                name: f.name,
                code: f.code,
                clientId: f.clientId,
                groupName: f.groupName,
                tier: f.tier,
                hubspotUrl: f.hubspotUrl,
              })
            }
            fields={[
              {
                key: 'name',
                label: 'Name',
                read: project.name,
                edit: (f, set) => (
                  <input
                    value={f.name}
                    onChange={(e) => set({ name: e.target.value })}
                  />
                ),
              },
              {
                key: 'code',
                label: 'Code',
                read: project.code,
                edit: (f, set) => (
                  <input
                    value={f.code}
                    onChange={(e) => set({ code: e.target.value })}
                  />
                ),
              },
              {
                key: 'client',
                label: 'Client',
                read: project.clientName,
                edit: canManageAll
                  ? (f, set) => (
                      <Select
                        ariaLabel="Client"
                        value={f.clientId}
                        onChange={(clientId) => set({ clientId })}
                        options={data.clients.map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                      />
                    )
                  : undefined,
              },
              {
                key: 'group',
                label: 'Group',
                read: orDash(project.groupName),
                edit: (f, set) => (
                  <input
                    value={f.groupName}
                    maxLength={60}
                    onChange={(e) => set({ groupName: e.target.value })}
                  />
                ),
              },
              {
                key: 'tier',
                label: 'Tier',
                read: orDash(currentTier && `Tier ${currentTier}`),
                edit: (f, set) => (
                  <TierSelect
                    value={f.tier}
                    onChange={(tier) => set({ tier })}
                  />
                ),
              },
              {
                key: 'hubspot',
                label: 'HubSpot',
                read: project.hubspotUrl ? (
                  <a
                    href={project.hubspotUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in HubSpot ↗
                  </a>
                ) : null,
                edit: (f, set) => (
                  <input
                    type="text"
                    inputMode="url"
                    value={f.hubspotUrl}
                    onChange={(e) => set({ hubspotUrl: e.target.value })}
                  />
                ),
              },
            ]}
          />

          <EditableGroup
            title="Roles"
            {...groupLock('roles')}
            initialForm={() => ({
              managerIds: detail.managerIds || [],
              accountManagerIds: detail.accountManagerIds || [],
              supportTeamIds: detail.supportTeamIds || [],
              technicalLeadIds: detail.technicalLeadIds || [],
            })}
            onSave={async (f) => {
              await putProject({ managerIds: f.managerIds });
              await putProfile({
                accountManagerIds: f.accountManagerIds,
                supportTeamIds: f.supportTeamIds,
                technicalLeadIds: f.technicalLeadIds,
              });
            }}
            fields={[
              {
                key: 'delivery',
                label: 'Delivery',
                read: namesFor(users, detail.managerIds).join(', ') || null,
                // A project manager may add managers but only an admin removes
                // one, so a PM's picker keeps the current managers fixed.
                edit: (f, set) => (
                  <UserMultiSelect
                    users={users}
                    selected={f.managerIds}
                    locked={canManageAll ? [] : detail.managerIds || []}
                    lockedHint="Only an admin can remove a project manager"
                    onChange={(managerIds) => set({ managerIds })}
                  />
                ),
              },
              {
                key: 'accountManagers',
                label: 'Account managers',
                read:
                  namesFor(users, detail.accountManagerIds).join(', ') || null,
                edit: (f, set) => (
                  <UserMultiSelect
                    users={users}
                    selected={f.accountManagerIds}
                    onChange={(accountManagerIds) => set({ accountManagerIds })}
                  />
                ),
              },
              {
                key: 'supportTeam',
                label: 'Support team',
                read: namesFor(users, detail.supportTeamIds).join(', ') || null,
                edit: (f, set) => (
                  <UserMultiSelect
                    users={users}
                    selected={f.supportTeamIds}
                    onChange={(supportTeamIds) => set({ supportTeamIds })}
                  />
                ),
              },
              {
                key: 'technicalLeads',
                label: 'Technical leads',
                read:
                  namesFor(users, detail.technicalLeadIds).join(', ') || null,
                edit: (f, set) => (
                  <UserMultiSelect
                    users={users}
                    selected={f.technicalLeadIds}
                    onChange={(technicalLeadIds) => set({ technicalLeadIds })}
                  />
                ),
              },
            ]}
          />

          <EditableGroup
            title="Timeline"
            {...groupLock('timeline')}
            initialForm={() => ({
              startDate: project.startDate || '',
              endDate: project.endDate || '',
              status: project.status,
              lifecycleStage: detail.lifecycleStage || '',
            })}
            onSave={(f) =>
              // This group writes the schedule anchors too — run it under the
              // shared schedule lock so it can't race the Memory timeline.
              lockSchedule(async () => {
                await putProject({
                  startDate: f.startDate || null,
                  endDate: f.endDate || null,
                  status: f.status,
                });
                await putProfile({
                  lifecycleStage: f.lifecycleStage || null,
                });
              })
            }
            fields={[
              {
                key: 'startDate',
                label: 'Start date',
                read: orDash(project.startDate),
                edit: (f, set) => (
                  <DatePicker
                    ariaLabel="Start date"
                    value={f.startDate}
                    onChange={(startDate) => set({ startDate })}
                  />
                ),
              },
              {
                key: 'endDate',
                label: 'End date',
                read: orDash(project.endDate),
                edit: (f, set) => (
                  <DatePicker
                    ariaLabel="End date"
                    value={f.endDate}
                    onChange={(endDate) => set({ endDate })}
                  />
                ),
              },
              {
                key: 'status',
                label: 'Status',
                read: <StatusBadge status={project.status} />,
                edit: (f, set) => (
                  <Select
                    ariaLabel="Status"
                    value={f.status}
                    onChange={(status) => set({ status })}
                    options={STATUS_OPTIONS}
                  />
                ),
              },
              {
                key: 'lifecycleStage',
                label: 'Lifecycle phase',
                read: LIFECYCLE_LABEL[detail.lifecycleStage] || null,
                edit: (f, set) => (
                  <Select
                    ariaLabel="Lifecycle phase"
                    value={f.lifecycleStage}
                    onChange={(lifecycleStage) => set({ lifecycleStage })}
                    options={LIFECYCLE_OPTIONS}
                  />
                ),
              },
              {
                key: 'next',
                label: 'Next milestone',
                read: orDash(nextMs),
              },
            ]}
          />

          <EditableGroup
            title="Commercial"
            {...groupLock('commercial')}
            initialForm={() => ({
              budgetMD: project.budgetMD,
              billable: project.billable,
              supportProject: project.supportProject,
            })}
            onSave={(f) =>
              putProject({
                budgetMD: Number(f.budgetMD),
                billable: f.billable,
                supportProject: f.supportProject,
              })
            }
            fields={[
              {
                key: 'budget',
                label: 'Budget',
                read: hasBudget ? `${project.budgetMD} MD` : null,
                edit: (f, set) => (
                  <input
                    type="number"
                    value={f.budgetMD}
                    onChange={(e) => set({ budgetMD: e.target.value })}
                  />
                ),
              },
              {
                key: 'spent',
                label: 'Spent',
                read: `${project.spentMD.toFixed(1)} MD`,
              },
              {
                key: 'remaining',
                label: 'Remaining',
                read: hasBudget
                  ? `${(project.budgetMD - project.spentMD).toFixed(1)} MD`
                  : null,
              },
              {
                key: 'workType',
                label: 'Work type',
                read: project.billable ? 'Client work' : 'Non-client work',
                edit: (f, set) => (
                  <Select
                    ariaLabel="Work type"
                    value={f.billable ? 'yes' : 'no'}
                    onChange={(next) => set({ billable: next === 'yes' })}
                    options={BILLABLE_OPTIONS}
                  />
                ),
              },
              {
                key: 'support',
                label: 'Support project',
                read: project.supportProject ? 'Yes' : 'No',
                edit: (f, set) => (
                  <Select
                    ariaLabel="Support project"
                    value={f.supportProject ? 'yes' : 'no'}
                    onChange={(next) => set({ supportProject: next === 'yes' })}
                    options={YES_NO_OPTIONS}
                  />
                ),
              },
            ]}
          />

          <EditableGroup
            title="Logging guidance"
            {...groupLock('guidance')}
            initialForm={() => ({
              loggingHint: project.loggingHint || '',
              loggingHintUrl: project.loggingHintUrl || '',
              absence: absenceChoice(project),
            })}
            onSave={(f) => {
              const loggingHintUrl = normalizeUrl(f.loggingHintUrl);
              if (loggingHintUrl && !isWebUrl(loggingHintUrl)) {
                throw new Error('Guide link must be an http(s) URL.');
              }
              return putProject({
                loggingHint: f.loggingHint.trim(),
                loggingHintUrl,
                ...(canManageAll
                  ? {
                      absence: f.absence !== 'no',
                      absencePaid: f.absence !== 'unpaid',
                    }
                  : {}),
              });
            }}
            fields={[
              {
                key: 'absence',
                label: 'Absence (can be logged ahead)',
                read: ABSENCE_OPTIONS.find(
                  (option) => option.value === absenceChoice(project),
                ).label,
                // Only admins decide what may be logged in the future, and
                // whether it counts toward total hours (paid) or not.
                edit: canManageAll
                  ? (f, set) => (
                      <Select
                        ariaLabel="Absence"
                        value={f.absence}
                        onChange={(next) => set({ absence: next })}
                        options={ABSENCE_OPTIONS}
                      />
                    )
                  : undefined,
              },
              {
                key: 'loggingHint',
                label: 'Shown when logging time',
                read: orDash(project.loggingHint),
                edit: (f, set) => (
                  <textarea
                    rows={4}
                    maxLength={500}
                    value={f.loggingHint}
                    placeholder="How to log time on this project"
                    onChange={(e) => set({ loggingHint: e.target.value })}
                  />
                ),
              },
              {
                key: 'loggingHintUrl',
                label: 'Full guide',
                read: project.loggingHintUrl ? (
                  <a
                    href={project.loggingHintUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open guide ↗
                  </a>
                ) : null,
                edit: (f, set) => (
                  <input
                    type="text"
                    inputMode="url"
                    value={f.loggingHintUrl}
                    onChange={(e) => set({ loggingHintUrl: e.target.value })}
                  />
                ),
              },
            ]}
          />
        </div>
      </section>

      <EditableList
        title="Key stakeholders"
        items={detail.stakeholders}
        fields={STAKEHOLDER_FIELDS}
        addLabel="Add stakeholder"
        emptyLabel="No stakeholders recorded yet."
        toForm={(s) => ({
          name: s.name || '',
          role: s.role || '',
          organisation: s.organisation || '',
          preferredChannel: s.preferredChannel || '',
          responsibility: s.responsibility || '',
        })}
        fromForm={(f) => ({
          name: (f.name || '').trim(),
          role: (f.role || '').trim(),
          organisation: (f.organisation || '').trim(),
          preferredChannel: f.preferredChannel || '',
          responsibility: (f.responsibility || '').trim(),
        })}
        keepIf={(p) => p.name}
        onReplace={saveList('stakeholders')}
        renderItem={(s) => (
          <>
            <b>{s.name}</b>
            <small>
              {[s.role, s.organisation].filter(Boolean).join(' · ')}
            </small>
            {s.responsibility && <p>{s.responsibility}</p>}
            {s.preferredChannel && (
              <span className="stakeholder-channel">
                Preferred: {s.preferredChannel}
              </span>
            )}
          </>
        )}
      />

      <EditableList
        title="Payment milestones"
        items={detail.paymentMilestones}
        fields={MILESTONE_FIELDS}
        addLabel="Add milestone"
        emptyLabel="No payment milestones recorded yet."
        toForm={(m) => ({
          milestoneType: m.milestoneType || '',
          plannedDate: m.plannedDate || '',
          amount: m.amount || '',
          currency: m.currency || '',
          status: m.status || 'Planned',
          note: m.note || '',
        })}
        fromForm={(f) => ({
          milestoneType: (f.milestoneType || '').trim(),
          plannedDate: f.plannedDate || null,
          amount: (f.amount || '').trim(),
          currency: (f.currency || '').trim(),
          status: f.status || 'Planned',
          note: (f.note || '').trim(),
        })}
        keepIf={(p) => p.milestoneType}
        externalBusy={savingPayments}
        onReplace={savePayments}
        renderItem={(m) => (
          <>
            <b>{m.milestoneType}</b>
            <small>
              {[
                m.plannedDate,
                [m.amount, m.currency].filter(Boolean).join(' '),
                m.status,
              ]
                .filter(Boolean)
                .join(' · ')}
            </small>
            {m.note && <p>{m.note}</p>}
          </>
        )}
      />

      <ProjectResources
        resources={detail.resources}
        toForm={resourceToForm}
        fromForm={resourceFromForm}
        onReplace={saveList('resources')}
      />
    </div>
  );
}
